const BLOCKED_SELECTOR = [
  "a",
  "button",
  "input",
  "select",
  "textarea",
  "iframe",
  "form",
  "script",
  "style",
  "object",
  "embed",
  "svg",
  "video",
  "audio",
  "link",
  "meta",
  "img",
  "picture",
  "source",
  "canvas",
].join(",");

const stripDangerousUrlAttributes = (html) =>
  String(html || "").replace(/\s+(href|src)\s*=\s*(["'])?\s*javascript:[^"'\s>]*/gi, "");

const unwrapNode = (node) => {
  if (!node?.parentNode) return;
  while (node.firstChild) node.parentNode.insertBefore(node.firstChild, node);
  node.remove();
};

const hasStyleValue = (node, pattern) => pattern.test(String(node?.getAttribute?.("style") || ""));
const hasBoldStyle = (node) => {
  const style = String(node?.getAttribute?.("style") || "").toLowerCase();
  const match = style.match(/font-weight\s*:\s*([^;]+)/);
  if (!match) return false;
  const value = match[1].trim();
  return value === "bold" || value === "bolder" || Number(value) >= 600;
};
const hasItalicStyle = (node) => hasStyleValue(node, /font-style\s*:\s*italic/i);
const hasUnderlineStyle = (node) => hasStyleValue(node, /text-decoration(?:-line)?\s*:\s*[^;]*underline/i);

const wrapNodeContent = (node, tagName) => {
  const wrapper = document.createElement(tagName);
  while (node.firstChild) wrapper.appendChild(node.firstChild);
  node.appendChild(wrapper);
  return wrapper;
};

const renameTag = (node, tagName) => {
  if (!node || node.nodeName === tagName.toUpperCase()) return node;
  const replacement = document.createElement(tagName);
  while (node.firstChild) replacement.appendChild(node.firstChild);
  node.replaceWith(replacement);
  return replacement;
};

const normalizeCopiedFormatting = (root) => {
  if (!root || typeof root.querySelectorAll !== "function") return;

  root.querySelectorAll("b").forEach((node) => renameTag(node, "strong"));
  root.querySelectorAll("i").forEach((node) => renameTag(node, "em"));

  root.querySelectorAll("span[style], font[style]").forEach((node) => {
    const wantsBold = hasBoldStyle(node);
    const wantsItalic = hasItalicStyle(node);
    const wantsUnderline = hasUnderlineStyle(node);

    if (!wantsBold && !wantsItalic && !wantsUnderline) return;

    let cursor = node;
    if (wantsBold) cursor = wrapNodeContent(cursor, "strong");
    if (wantsItalic) cursor = wrapNodeContent(cursor, "em");
    if (wantsUnderline) wrapNodeContent(cursor, "u");
  });
};

const scrubDreamHtmlRoot = (root) => {
  if (!root || typeof root.querySelectorAll !== "function") return;

  root.querySelectorAll(BLOCKED_SELECTOR).forEach((node) => {
    const text = node.textContent || "";
    node.replaceWith(document.createTextNode(text));
  });

  normalizeCopiedFormatting(root);

  root.querySelectorAll("*").forEach((node) => {
    Array.from(node.attributes || []).forEach((attribute) => {
      node.removeAttribute(attribute.name);
    });
  });

  root.querySelectorAll("span, font").forEach((node) => unwrapNode(node));
};

export const scrubDreamEditor = () => {
  // Celowo nie czyścimy żywego contentEditable podczas pisania.
  // Mutowanie DOM-u edytora po input/toolbar powodowało przeskoki kursora.
  // Bezpieczna sanitizacja działa na kopii HTML w sanitizeDreamHtml().
};

export const sanitizeDreamHtml = (html) => {
  if (typeof document === "undefined") {
    return stripDangerousUrlAttributes(html)
      .replace(/<\/?(?:a|button|input|select|textarea|iframe|form|script|style|object|embed|svg|video|audio|link|meta|img|picture|source|canvas)[^>]*>/gi, "")
      .replace(/<\/?b\b/gi, (match) => match.replace(/b/i, "strong"))
      .replace(/<\/?i\b/gi, (match) => match.replace(/i/i, "em"))
      .trim();
  }

  const template = document.createElement("template");
  template.innerHTML = stripDangerousUrlAttributes(html);
  scrubDreamHtmlRoot(template.content);
  return template.innerHTML.trim();
};

export const extractDreamText = (html) => {
  const safeHtml = sanitizeDreamHtml(html);

  if (typeof document === "undefined") {
    return String(safeHtml || "")
      .replace(/<[^>]*>/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  }

  const template = document.createElement("template");
  template.innerHTML = safeHtml;
  return (template.content.textContent || "").replace(/\s+/g, " ").trim();
};

export const insertPlainTextAtSelection = (text) => {
  const value = String(text || "");
  if (!value) return;

  try {
    document.execCommand("insertText", false, value);
    return;
  } catch (_) {}

  const selection = window.getSelection?.();
  if (!selection || !selection.rangeCount) return;

  selection.deleteFromDocument();
  selection.getRangeAt(0).insertNode(document.createTextNode(value));
  selection.collapseToEnd();
};

export const handlePlainTextPaste = (event, editor) => {
  const clipboard = event.clipboardData || window.clipboardData;
  if (!clipboard) return false;

  const text = clipboard.getData("text/plain");
  const hasFiles = Array.from(clipboard.files || []).length > 0;
  const hasHtml = !!clipboard.getData("text/html");

  if (typeof text === "string" && text.length > 0) {
    event.preventDefault();
    editor?.focus?.();
    insertPlainTextAtSelection(text);
    return true;
  }

  if (hasFiles || hasHtml) {
    event.preventDefault();
    return true;
  }

  return false;
};

export const blockDreamEditorDrop = (event) => {
  event.preventDefault();
};
